import React from 'react';

import { StageRouteGeometry } from '../services/fleet';

type LeafletModule = typeof import('leaflet');
type ReactLeafletModule = typeof import('react-leaflet');

const COLORS = ['#38bdf8', '#a78bfa', '#f59e0b', '#22d3ee'];

const escapeHtml = (value: string): string => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

function approxSegmentLength(a: [number, number], b: [number, number]): number {
  const meanLat = ((a[0] + b[0]) / 2) * Math.PI / 180;
  const y = b[0] - a[0];
  const x = (b[1] - a[1]) * Math.cos(meanLat);
  return Math.hypot(x, y);
}

/** Arc-length point for a route callout. Geodesic precision is unnecessary;
 * this only keeps labels near a stable, visually central part of the line. */
export function routePointAtFraction(
  points: Array<[number, number]>,
  fraction = 0.5,
): [number, number] | null {
  if (points.length === 0) return null;
  if (points.length === 1) return points[0];
  const lengths = points.slice(1).map((point, index) =>
    approxSegmentLength(points[index], point));
  const total = lengths.reduce((sum, length) => sum + length, 0);
  if (total <= 0) return points[Math.floor(points.length / 2)];
  const target = Math.max(0, Math.min(1, fraction)) * total;
  let traversed = 0;
  for (let index = 0; index < lengths.length; index += 1) {
    const next = traversed + lengths[index];
    if (next >= target) {
      const ratio = lengths[index] > 0 ? (target - traversed) / lengths[index] : 0;
      return [
        points[index][0] + (points[index + 1][0] - points[index][0]) * ratio,
        points[index][1] + (points[index + 1][1] - points[index][1]) * ratio,
      ];
    }
    traversed = next;
  }
  return points[points.length - 1];
}

export function formatRouteDistance(lengthM: number | null): string | null {
  if (lengthM == null || !Number.isFinite(lengthM) || lengthM <= 0) return null;
  return lengthM >= 1000
    ? `${(lengthM / 1000).toFixed(1)} km`
    : `${Math.round(lengthM)} m`;
}

interface CalloutCandidate {
  route: StageRouteGeometry;
  position: [number, number];
  x: number;
  y: number;
  selected: boolean;
  edge?: 'left' | 'right' | null;
}

/** Greedy screen-space declutter. The selected stage always wins, then
 * stable stage order fills only badges whose boxes do not collide. */
export function declutterRouteCallouts(
  candidates: CalloutCandidate[],
  compact: boolean,
  zoom: number,
): CalloutCandidate[] {
  const xGap = compact ? 39 : 108;
  const yGap = compact ? 25 : 27;
  const maxVisible = compact
    ? zoom <= 11 ? 8 : zoom === 12 ? 12 : 18
    : zoom <= 11 ? 12 : 24;
  const accepted: CalloutCandidate[] = [];
  const ordered = [...candidates].sort(
    (a, b) => Number(b.selected) - Number(a.selected)
      || a.route.stage_index - b.route.stage_index,
  );
  for (const candidate of ordered) {
    const collides = accepted.some((other) =>
      Math.abs(candidate.x - other.x) < xGap
      && Math.abs(candidate.y - other.y) < yGap);
    if (!collides) accepted.push(candidate);
    if (accepted.length >= maxVisible) break;
  }
  return accepted;
}

const RouteCallouts: React.FC<{
  L: LeafletModule;
  RL: ReactLeafletModule;
  routes: StageRouteGeometry[];
  selectedStage: number | null;
  pane: string;
  onSelectStage: (stageIndex: number) => void;
}> = ({ L, RL, routes, selectedStage, pane, onSelectStage }) => {
  const { Marker, useMap } = RL as any;
  const map = useMap();
  const [viewRevision, setViewRevision] = React.useState(0);
  React.useEffect(() => {
    const refresh = () => setViewRevision((value) => value + 1);
    map.on('zoomend resize moveend', refresh);
    return () => map.off('zoomend resize moveend', refresh);
  }, [map]);

  const callouts = React.useMemo(() => {
    const zoom = map.getZoom();
    const compact = map.getSize().x < 640;
    const fractions = [0.34, 0.46, 0.58, 0.7];
    const candidates = routes.flatMap((route): CalloutCandidate[] => {
      const selected = selectedStage === route.stage_index;
      const position = routePointAtFraction(
        route.points,
        selected ? 0.5 : fractions[Math.abs(route.stage_index) % fractions.length],
      );
      if (!position) return [];
      const projected = map.project(position, zoom);
      const containerPoint = map.latLngToContainerPoint(position);
      const mapWidth = map.getSize().x;
      // The desktop cars panel overlays the rightmost 400px of the full-width map.
      // Treat its edge as the visible map edge so route labels stay readable.
      const visibleMapWidth = mapWidth >= 1024 ? mapWidth - 400 : mapWidth;
      const edgeMargin = compact ? 45 : 120;
      const edge = containerPoint.x < edgeMargin
        ? 'left'
        : containerPoint.x > visibleMapWidth - edgeMargin
          ? 'right'
          : null;
      return [{ route, position, x: projected.x, y: projected.y, selected, edge }];
    });
    return declutterRouteCallouts(candidates, compact, zoom);
  }, [map, routes, selectedStage, viewRevision]);

  return (
    <>
      {callouts.map(({ route, position, selected, edge }) => {
        const color = selected
          ? '#fbbf24'
          : COLORS[Math.abs(route.stage_index) % COLORS.length];
        const stageLabel = `SS${route.stage_index + 1}`;
        const routeName = route.name?.trim() || null;
        const distance = formatRouteDistance(route.length_m);
        const detail = [routeName && routeName !== stageLabel ? routeName : null, distance]
          .filter(Boolean)
          .join(' · ');
        const icon = L.divIcon({
          className: 'tv-stage-route-callout-icon',
          iconAnchor: [0, 0],
          html:
            `<div class="tv-stage-route-callout${selected ? ' is-selected' : ''}${
              edge ? ` is-edge-${edge}` : ''
            }" `
            + `style="--route-color:${color}" aria-label="${escapeHtml(
              [stageLabel, detail].filter(Boolean).join(', '),
            )}">`
            + `<span class="tv-stage-route-callout-stage">${escapeHtml(stageLabel)}</span>`
            + (detail
              ? `<span class="tv-stage-route-callout-detail">${escapeHtml(detail)}</span>`
              : '')
            + '</div>',
        });
        return (
          <Marker
            key={route.stage_index}
            pane={pane}
            position={position}
            icon={icon}
            zIndexOffset={selected ? 100 : 0}
            interactive
            keyboard
            riseOnHover
            title={`Focus ${stageLabel}${detail ? `, ${detail}` : ''}`}
            eventHandlers={{ click: () => onSelectStage(route.stage_index) }}
          />
        );
      })}
    </>
  );
};

const StageRoutesOverlayComponent: React.FC<{
  L: LeafletModule;
  RL: ReactLeafletModule;
  routes: StageRouteGeometry[];
  selectedStage: number | null;
  pane: string;
  onSelectStage: (stageIndex: number) => void;
}> = ({ L, RL, routes, selectedStage, pane, onSelectStage }) => {
  const { Polyline, Tooltip } = RL as any;
  return (
    <>
      {routes.map((route) => {
        const selected = selectedStage === route.stage_index;
        const color = selected
          ? '#fbbf24'
          : COLORS[Math.abs(route.stage_index) % COLORS.length];
        const stageLabel = `SS${route.stage_index + 1}`;
        const routeName = route.name?.trim() || null;
        const hoverLabel = routeName || stageLabel;
        return (
          <React.Fragment key={route.stage_index}>
            <Polyline
              pane={pane}
              positions={route.points}
              pathOptions={{
                color: '#070b12',
                weight: selected ? 8 : 6,
                opacity: selected ? 0.9 : 0.62,
                lineCap: 'round',
                lineJoin: 'round',
                interactive: false,
              }}
            />
            <Polyline
              pane={pane}
              positions={route.points}
              pathOptions={{
                color,
                weight: selected ? 4 : 2.5,
                opacity: selected ? 0.95 : 0.62,
                lineCap: 'round',
                lineJoin: 'round',
              }}
            >
              <Tooltip sticky>{hoverLabel}</Tooltip>
            </Polyline>
          </React.Fragment>
        );
      })}
      <RouteCallouts
        L={L}
        RL={RL}
        routes={routes}
        selectedStage={selectedStage}
        pane={pane}
        onSelectStage={onSelectStage}
      />
    </>
  );
};

export const StageRoutesOverlay = React.memo(StageRoutesOverlayComponent);

export default StageRoutesOverlay;
